import React, { useState, useEffect, useRef, useMemo } from "react";
import "./IdeasPage.scss";
import { useIdeasViewModel } from "../viewmodel/useIdeasViewModel";
import { fetchIdeaInvitationsRequestsRequested } from "../store";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import Loader from "@/core/components/loader/Loader";
import {
  IdeasFilter,
  ViewMode,
  SortOption,
  IdeaStatusFilter,
} from "../model/types";
import { FiltersDrawer, FilterState } from "../components/FiltersDrawer";
import { IdeaListItem } from "../components/IdeaListItem";
import { IdeaGridItem } from "../components/IdeaGridItem";
import { useUnit } from "effector-react";
import {
  $sectorsTerms,
  $expertiseTerms,
  $lookingForTerms,
  $programmeTerms,
} from "@/core/store/terms";
import { $ideasStore } from "../store";
import { useToast } from "@/core/hooks/useToast";
import Tracking from "@/core/utils/tracking";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { $userBasicInfo } from "@/features/user/store";
import { SEO } from "@/core/components/SEO/SEO";
import { imageList } from "@/core/utils/imageList";

const sortOptions = {
  created: "Recently added",
  most_popular: "Most popular",
  deadline_desc: "Deadline (latest first)",
  deadline_asc: "Deadline (earliest first)",
};

export const IdeasPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState<IdeasFilter>(() => {
    // Initialize from URL
    return location.pathname === "/ideas/my-ideas" ? "my-ideas" : "all";
  });
  const [statusFilter, setStatusFilter] =
    useState<IdeaStatusFilter>("published");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [sortBy, setSortBy] = useState<SortOption>("created");
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [allIdeasSearchQuery, setAllIdeasSearchQuery] = useState<string>("");
  const [myIdeasSearchQuery, setMyIdeasSearchQuery] = useState<string>("");
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const userBasicInfo = useUnit($userBasicInfo);

  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    fromDate: "",
    toDate: "",
    categories: [],
    programmes: [],
    sectors: [],
    expertise: [],
    lookingFor: [],
    categorySearch: "",
    programmeSearch: "",
    sectorSearch: "",
  });
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

  const {
    loading,
    error,
    getFilteredIdeas,
    getPublishedIdeas,
    getDraftIdeas,
    getArchivedIdeas,
    getAllIdeasCount,
    getTotalIdeasCount,
    getPublishedIdeasCount,
    getDraftIdeasCount,
    getArchivedIdeasCount,
    fetchIdeasWithFilters,
    fetchPublishedIdeas,
    fetchDraftIdeas,
    fetchArchivedIdeas,
    loadMoreIdeasWithFilters,
    hasMoreIdeas,
    loadMore,
    updateBookmark,
  } = useIdeasViewModel();
  const { showError } = useToast();

  useEffect(() => {
    Tracking.pageView(
      "Ideas",
      location.pathname,
      undefined,
      undefined,
      undefined,
      document.title,
      undefined,
      undefined,
    );

    // If URL is /ideas/my-ideas, fetch my-ideas data on mount
    if (location.pathname === "/ideas/my-ideas") {
      fetchIdeasWithFilters(
        {
          sort: sortBy,
          search: myIdeasSearchQuery || undefined,
          filters: selectedFilters,
        },
        "my-ideas",
      );
      fetchIdeaInvitationsRequestsRequested();
      setViewMode("list");
    }
  }, []);

  // Get terms for displaying names in filter chips and ideas store for invitations
  const [
    sectorsTerms,
    expertiseTerms,
    lookingForTerms,
    programmeTerms,
    ideasStore,
  ] = useUnit([
    $sectorsTerms,
    $expertiseTerms,
    $lookingForTerms,
    $programmeTerms,
    $ideasStore,
  ]);

  // Memoize terms maps for efficient lookups
  const termsLookupMaps = useMemo(() => {
    const sectorsMap = new Map();
    const expertiseMap = new Map();
    const lookingForMap = new Map();
    const programmesMap = new Map();

    if (sectorsTerms.data) {
      sectorsTerms.data.forEach((term) => {
        if (term && term.tid) {
          sectorsMap.set(term.tid, term.name || term.tid);
        }
      });
    }

    if (expertiseTerms.data) {
      expertiseTerms.data.forEach((term) => {
        if (term && term.tid) {
          expertiseMap.set(term.tid, term.name || term.tid);
        }
      });
    }

    if (lookingForTerms.data) {
      lookingForTerms.data.forEach((term) => {
        if (term && term.tid) {
          lookingForMap.set(term.tid, term.name || term.tid);
        }
      });
    }

    if (programmeTerms.data) {
      programmeTerms.data.forEach((term) => {
        if (term && term.tid) {
          programmesMap.set(term.tid, term.name || term.tid);
        }
      });
    }

    return {
      sectors: sectorsMap,
      expertise: expertiseMap,
      lookingFor: lookingForMap,
      programmes: programmesMap,
    };
  }, [
    sectorsTerms.data,
    expertiseTerms.data,
    lookingForTerms.data,
    programmeTerms.data,
  ]);

  // Helper function to get term name by tid using memoized maps
  const getTermName = (
    tid: string,
    type: "sectors" | "expertise" | "lookingFor" | "programmes",
  ): string => {
    if (!tid || typeof tid !== "string") {
      return String(tid || "");
    }

    return termsLookupMaps[type].get(tid) || String(tid);
  };

  const handleFilterChange = (filter: IdeasFilter) => {
    setActiveFilter(filter);
    setViewMode("grid");

    // Update URL based on filter
    if (filter === "my-ideas") {
      navigate("/ideas/my-ideas", { replace: true });
    } else {
      navigate("/ideas", { replace: true });
    }

    // Store current search query for the current filter and load the new filter's search query
    if (activeFilter === "all") {
      setAllIdeasSearchQuery(searchQuery);
    } else if (activeFilter === "my-ideas") {
      setMyIdeasSearchQuery(searchQuery);
    }

    // Load the search query for the new filter
    const newSearchQuery =
      filter === "all" ? allIdeasSearchQuery : myIdeasSearchQuery;
    setSearchQuery(newSearchQuery);

    // console.log(`Switched to filter: ${filter}`);

    // Fetch ideas based on filter type with the appropriate search query
    fetchIdeasWithFilters(
      {
        sort: sortBy,
        search: newSearchQuery || undefined,
        filters: selectedFilters,
      },
      filter,
    );

    // If filter is 'my-ideas', also fetch idea invitations requests
    if (filter === "my-ideas") {
      fetchIdeaInvitationsRequestsRequested();
      setViewMode("list");
    }
  };

  const handleStatusFilterChange = (status: IdeaStatusFilter) => {
    setStatusFilter(status);
    // console.log(`Switched to status filter: ${status}`);
    // Fetch specific status ideas when switching tabs with the current search query
    switch (status) {
      case "published":
        fetchPublishedIdeas({
          sort: sortBy,
          search: searchQuery || undefined,
          filters: selectedFilters,
        });
        break;
      case "drafts":
        fetchDraftIdeas({
          sort: sortBy,
          search: searchQuery || undefined,
          filters: selectedFilters,
        });
        break;
      case "archived":
        fetchArchivedIdeas({
          sort: sortBy,
          search: searchQuery || undefined,
          filters: selectedFilters,
        });
        break;
    }
  };

  const toggleSectorExpansion = (ideaId: string) => {
    setExpandedSectors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(ideaId)) {
        newSet.delete(ideaId);
      } else {
        newSet.add(ideaId);
      }
      return newSet;
    });
  };

  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort);
    setIsSortDropdownOpen(false);
    fetchIdeasWithFilters(
      {
        sort: newSort,
        search: searchQuery || undefined,
        filters: selectedFilters,
      },
      activeFilter,
    );
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (activeFilter === "all") {
        // For 'all' filter, fetch all ideas with search
        fetchIdeasWithFilters(
          {
            sort: sortBy,
            search: searchQuery || undefined,
            filters: selectedFilters,
          },
          activeFilter,
        );
      } else if (activeFilter === "my-ideas") {
        // For 'my-ideas' filter, fetch the current status tab with search
        handleStatusFilterChange(statusFilter);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleLoadMore = () => {
    loadMoreIdeasWithFilters({
      sort: sortBy,
      search: searchQuery || undefined,
      filters: selectedFilters,
    });
  };

  const handleBookmark = async (ideaId: string, isBookmarked: boolean) => {
    try {
      updateBookmark({ ideaId, isBookmarked: !isBookmarked });
    } catch (error: any) {
      console.error("Error updating bookmark:", error);
      showError(error?.error || "Failed to update bookmark");
      updateBookmark({ ideaId, isBookmarked: isBookmarked });
    }
  };

  const handleActionComplete = () => {
    // Refetch idea invitations/requests when an action is completed
    fetchIdeaInvitationsRequestsRequested();
    // Also refetch the main ideas list to ensure everything is up to date
    fetchIdeasWithFilters(
      {
        sort: sortBy,
        search: searchQuery || undefined,
        filters: selectedFilters,
      },
      activeFilter,
    );
  };

  const handleFiltersApply = (filters: FilterState) => {
    setSelectedFilters(filters);
    fetchIdeasWithFilters(
      {
        sort: sortBy,
        search: searchQuery || undefined,
        filters: filters,
      },
      activeFilter,
    );
  };

  const getActiveFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedFilters.fromDate || selectedFilters.toDate) count++;
    count += selectedFilters.categories.length;
    count += selectedFilters.programmes.length;
    count += selectedFilters.sectors.length;
    count += selectedFilters.expertise.length;
    count += selectedFilters.lookingFor.length;
    return count;
  }, [selectedFilters]);

  const removeFilter = (type: keyof FilterState, value: string) => {
    const newFilters = { ...selectedFilters };
    if (type === "fromDate" || type === "toDate") {
      newFilters[type] = "";
    } else if (Array.isArray(newFilters[type])) {
      (newFilters[type] as string[]) = (newFilters[type] as string[]).filter(
        (item) => item !== value,
      );
    }
    setSelectedFilters(newFilters);
    fetchIdeasWithFilters(
      {
        sort: sortBy,
        search: searchQuery || undefined,
        filters: newFilters,
      },
      activeFilter,
    );
  };

  const clearAllFilters = () => {
    const emptyFilters: FilterState = {
      fromDate: "",
      toDate: "",
      categories: [],
      programmes: [],
      sectors: [],
      expertise: [],
      lookingFor: [],
      categorySearch: "",
      programmeSearch: "",
      sectorSearch: "",
    };
    setSelectedFilters(emptyFilters);
    fetchIdeasWithFilters(
      {
        sort: sortBy,
        search: searchQuery || undefined,
        filters: emptyFilters,
      },
      activeFilter,
    );
  };

  // Memoize filter chips to prevent unnecessary re-renders
  const filterChips = useMemo(() => {
    const chips = [];

    if (selectedFilters.fromDate) {
      chips.push(
        <div key="fromDate" className="filter-chip">
          <span>From: {selectedFilters.fromDate}</span>
          <button
            onClick={() => removeFilter("fromDate", selectedFilters.fromDate)}
          >
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    }

    if (selectedFilters.toDate) {
      chips.push(
        <div key="toDate" className="filter-chip">
          <span>To: {selectedFilters.toDate}</span>
          <button
            onClick={() => removeFilter("toDate", selectedFilters.toDate)}
          >
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    }

    selectedFilters.categories.forEach((category) => {
      chips.push(
        <div key={`category-${category}`} className="filter-chip">
          <span>{category}</span>
          <button onClick={() => removeFilter("categories", category)}>
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    });

    selectedFilters.programmes.forEach((programme) => {
      const programmeId = String(programme);
      const programmeName = getTermName(programmeId, "programmes");
      chips.push(
        <div key={`programme-${programmeId}`} className="filter-chip">
          <span>{programmeName}</span>
          <button onClick={() => removeFilter("programmes", programmeId)}>
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    });

    selectedFilters.sectors.forEach((sector) => {
      const sectorId = String(sector);
      const sectorName = getTermName(sectorId, "sectors");
      chips.push(
        <div key={`sector-${sectorId}`} className="filter-chip">
          <span>{sectorName}</span>
          <button onClick={() => removeFilter("sectors", sectorId)}>
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    });

    selectedFilters.expertise.forEach((expertise) => {
      const expertiseId = String(expertise);
      const expertiseName = getTermName(expertiseId, "expertise");
      chips.push(
        <div key={`expertise-${expertiseId}`} className="filter-chip">
          <span>{expertiseName}</span>
          <button onClick={() => removeFilter("expertise", expertiseId)}>
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    });

    selectedFilters.lookingFor.forEach((item) => {
      const itemId = String(item);
      const itemName = getTermName(itemId, "lookingFor");
      chips.push(
        <div key={`looking-for-${itemId}`} className="filter-chip">
          <span>Looking for: {itemName}</span>
          <button onClick={() => removeFilter("lookingFor", itemId)}>
            <SvgIcon icon="icon-close" />
          </button>
        </div>,
      );
    });

    return chips;
  }, [selectedFilters, termsLookupMaps]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSortDropdownOpen(false);
      }
    };

    if (isSortDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortDropdownOpen]);

  const allFilteredIdeas = getFilteredIdeas(activeFilter);

  // Get invitation and request ideas separately for the dedicated section
  const getInvitationIdeas = () => {
    if (!ideasStore.invitations || !ideasStore.invitations.invitation) {
      return [];
    }
    return ideasStore.invitations.invitation.map((item) => {
      item.idea_data.action_id = item.action_id;
      return item.idea_data;
    });
  };

  const getRequestIdeas = () => {
    if (!ideasStore.invitations || !ideasStore.invitations.request) {
      return [];
    }
    return ideasStore.invitations.request.map((item) => {
      item.idea_data.action_id = item.action_id;
      return item.idea_data;
    });
  };

  // Get ideas by status using the separate arrays from store
  const getIdeasByStatus = (status: IdeaStatusFilter) => {
    switch (status) {
      case "published":
        return getPublishedIdeas();
      case "drafts":
        return getDraftIdeas();
      case "archived":
        return getArchivedIdeas();
      default:
        return [];
    }
  };

  const filteredIdeas =
    activeFilter === "my-ideas"
      ? getIdeasByStatus(statusFilter)
      : allFilteredIdeas;
  const invitationIdeas = getInvitationIdeas();
  const requestIdeas = getRequestIdeas();

  // Get counts for each status
  const getStatusCount = (status: IdeaStatusFilter) => {
    switch (status) {
      case "published":
        return getPublishedIdeasCount();
      case "drafts":
        return getDraftIdeasCount();
      case "archived":
        return getArchivedIdeasCount();
      default:
        return 0;
    }
  };

  if (loading && filteredIdeas.length === 0) {
    return (
      <div className="ideas-page">
        <div className="pageTitle">Ideas</div>
        <Loader overlay />
      </div>
    );
  }

  if (error) {
    return (
      <div className="ideas-page">
        <div className="pageTitle">Ideas</div>
        <div>Error loading ideas: {error}</div>
      </div>
    );
  }

  return (
    <div className="ideas-page">
      <SEO title={`Ideas | EUcalls`} description={"Ideas"} />

      <div className="pageTitle">Ideas</div>
      <div className="ideas-header">
        <div className="button-group">
          <button
            className={`button-group__item ${activeFilter === "all" ? "active" : ""}`}
            onClick={() => handleFilterChange("all")}
          >
            All ideas ({getAllIdeasCount()})
          </button>

          <button
            className={`button-group__item ${activeFilter === "my-ideas" ? "active" : ""}`}
            onClick={() => handleFilterChange("my-ideas")}
          >
            My ideas
          </button>
        </div>
      </div>
      <div className="idea-filters">
        <div className="filter-search">
          <input
            type="text"
            placeholder="Search"
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchSubmit}
          />
        </div>

        {activeFilter === "all" && (
          <div className="filter-controls">
            <button
              className={`filter-btn list-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              <SvgIcon
                icon={viewMode === "grid" ? "icon-list" : "icon-grid-view"}
              />
              {viewMode === "grid" ? "List view" : "Grid view"}
            </button>

            {/* <div className="sort-dropdown" ref={sortDropdownRef}>
            <button className="filter-btn sort-btn" onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}>
              <SvgIcon icon="icon-sort" />
              {sortOptions[sortBy]}
              <SvgIcon icon={isSortDropdownOpen ? 'icon-chevron-up' : 'icon-chevron-down'} />
            </button>

            {isSortDropdownOpen && (
              <div className="sort-dropdown-menu">
                {Object.entries(sortOptions).map(([key, label]) => (
                  <button
                    key={key}
                    className={`sort-option ${sortBy === key ? 'active' : ''}`}
                    onClick={() => handleSortChange(key as SortOption)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div> */}

            <button
              className="filter-btn filters-btn"
              onClick={() => setIsFiltersDrawerOpen(true)}
            >
              <SvgIcon icon="icon-filter" />
              Filters
              {getActiveFiltersCount > 0 && (
                <span className="filter-count">{getActiveFiltersCount}</span>
              )}
            </button>
          </div>
        )}
      </div>
      {activeFilter === "all" && getActiveFiltersCount > 0 && (
        <div className="ideas-selected-filters">
          <div className="selected-filters-header">
            {/* <button className="clear-all-btn" onClick={clearAllFilters}>
              Clear all
            </button> */}
          </div>
          <div className="filters-chips">{filterChips}</div>
        </div>
      )}
      {activeFilter === "all" && (
        <div className="ideas-results">{getTotalIdeasCount()} results</div>
      )}
      <div
        className={`ideas-content ${
          activeFilter === "my-ideas" &&
          invitationIdeas.length === 0 &&
          requestIdeas.length === 0 &&
          getStatusCount("archived") === 0 &&
          getStatusCount("published") === 0 &&
          getStatusCount("drafts") === 0
            ? "noContent"
            : ""
        }`}
      >
        {activeFilter === "my-ideas" &&
          invitationIdeas.length === 0 &&
          requestIdeas.length === 0 &&
          getStatusCount("archived") === 0 &&
          getStatusCount("published") === 0 &&
          getStatusCount("drafts") === 0 && (
            <div className="ideasPageNoContent">
              {typeof userBasicInfo?.partner === "string" &&
              userBasicInfo.partner === "freelancer" ? (
                <div className="ideasPageNoContent-icon">
                  <img
                    src={imageList.ideasNoContentBgIcon}
                    alt="ideasNoContentBgIcon"
                  />
                  <SvgIcon
                    icon="icon-idea-org2"
                    className="ideasPageNoContent-icon-svg-icon"
                  />
                </div>
              ) : (
                <div className="ideasPageNoContent-icon">
                  <img
                    src={imageList.ideasNoContentBgIcon}
                    alt="ideasNoContentBgIcon"
                  />

                  <SvgIcon
                    icon="icon-ideas-noContent"
                    className="ideasPageNoContent-icon-svg-icon"
                  />
                </div>
              )}
              <div className="ideasPageNoContent-text">
                <div className="ideasPageNoContent-text-title">
                  {typeof userBasicInfo?.partner === "string" &&
                  userBasicInfo.partner === "freelancer"
                    ? "Create your organisation to be able to share or join an idea"
                    : "You haven’t created or joined an idea yet."}
                </div>
                <div className="ideasPageNoContent-text-desc">
                  {typeof userBasicInfo?.partner === "string" &&
                  userBasicInfo.partner === "freelancer"
                    ? "This feature is only available to users with an organisation"
                    : "Start your idea by declaring interest in a call or request to join an existing idea."}
                </div>
                {typeof userBasicInfo?.partner === "string" &&
                userBasicInfo.partner === "freelancer" ? (
                  <div
                    className="ideasPageNoContent-text-actions"
                    onMouseEnter={() => {
                      setIsHovering(true);
                    }}
                    onMouseLeave={() => {
                      setIsHovering(false);
                    }}
                  >
                    <Link
                      to="/settings/organization"
                      className="ideasPageNoContent-text-actions-btnIdeas"
                    >
                      <SvgIcon
                        icon={
                          isHovering ? "icon-settings-blue" : "icon-settings"
                        }
                      />
                      Go to settings
                    </Link>
                  </div>
                ) : (
                  <div className="ideasPageNoContent-text-actions">
                    <Link
                      to="/calls"
                      className="ideasPageNoContent-text-actions-btnCalls"
                    >
                      View all calls
                    </Link>
                    <div
                      className="ideasPageNoContent-text-actions-btnIdeas"
                      onClick={() => {
                        handleFilterChange("all");
                      }}
                    >
                      View all ideas
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        {activeFilter === "my-ideas" &&
          (invitationIdeas.length > 0 || requestIdeas.length > 0) && (
            <div className="invitations-requests">
              {/* Invitations and Requests section for my-ideas */}
              {activeFilter === "my-ideas" &&
                (invitationIdeas.length > 0 || requestIdeas.length > 0) && (
                  <div className="invitations-requests-section">
                    {/* {invitationIdeas.length > 0 && (
                  <div className="invitations-section">
                    <h3 className="section-title">Invitations ({invitationIdeas.length})</h3>
                    <div className={`ideas-list ${viewMode === 'list' ? 'list-view' : 'grid-view'}`}>
                      {invitationIdeas.map((idea) => {
                        if (viewMode === 'list') {
                          // console.log('idea', idea);
                          return (
                            <IdeaListItem
                              key={`invitation-list-${idea.id}`}
                              idea={idea}
                              onBookmark={handleBookmark}
                              isMyIdea={true}
                              type="invitation"
                              onActionComplete={handleActionComplete}
                              action_id={idea.action_id}
                            />
                          );
                        } else {
                          return (
                            <IdeaGridItem key={`invitation-grid-${idea.id}`} idea={idea} onBookmark={handleBookmark} />
                          );
                        }
                      })}
                    </div>
                  </div>
                )} */}

                    <div className="requests-section">
                      <h3 className="section-title">
                        <SvgIcon icon="icon-ideas-invitations" />
                        Invitations and requests
                      </h3>
                      <div
                        className={`ideas-list ${viewMode === "list" ? "list-view" : "grid-view"}`}
                      >
                        {requestIdeas.length > 0 &&
                          requestIdeas.map((idea) => {
                            if (viewMode === "list") {
                              return (
                                <IdeaListItem
                                  key={`request-list-${idea.id}`}
                                  idea={idea}
                                  onBookmark={handleBookmark}
                                  isMyIdea={true}
                                  type="request"
                                  onActionComplete={handleActionComplete}
                                  action_id={idea.action_id}
                                />
                              );
                            } else {
                              return (
                                <IdeaGridItem
                                  key={`request-grid-${idea.id}`}
                                  idea={idea}
                                  onBookmark={handleBookmark}
                                />
                              );
                            }
                          })}
                        {invitationIdeas.length > 0 &&
                          invitationIdeas.map((idea) => {
                            if (viewMode === "list") {
                              return (
                                <IdeaListItem
                                  key={`invitation-list-${idea.id}`}
                                  idea={idea}
                                  onBookmark={handleBookmark}
                                  isMyIdea={true}
                                  type="invitation"
                                  onActionComplete={handleActionComplete}
                                  action_id={idea.action_id}
                                />
                              );
                            } else {
                              return (
                                <IdeaGridItem
                                  key={`request-grid-${idea.id}`}
                                  idea={idea}
                                  onBookmark={handleBookmark}
                                />
                              );
                            }
                          })}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          )}
        <div className="myIdeasContainer">
          {(invitationIdeas.length > 0 ||
            requestIdeas.length > 0 ||
            getStatusCount("archived") > 0 ||
            getStatusCount("published") > 0 ||
            getStatusCount("drafts") > 0) &&
            activeFilter === "my-ideas" && (
              <h3 className="section-title">
                <SvgIcon icon="icon-ideas" />
                My Ideas
              </h3>
            )}
          {(invitationIdeas.length > 0 ||
            requestIdeas.length > 0 ||
            getStatusCount("archived") > 0 ||
            getStatusCount("published") > 0 ||
            getStatusCount("drafts") > 0) &&
            activeFilter === "my-ideas" && (
              <div className="status-tabs-container">
                <div className="status-tabs">
                  <button
                    className={`status-tab ${statusFilter === "published" ? "active" : ""}`}
                    onClick={() => handleStatusFilterChange("published")}
                  >
                    <span></span> Published ({getStatusCount("published")})
                  </button>
                  <button
                    className={`status-tab ${statusFilter === "drafts" ? "active" : ""}`}
                    onClick={() => handleStatusFilterChange("drafts")}
                  >
                    Drafts ({getStatusCount("drafts")})
                  </button>
                  <button
                    className={`status-tab ${statusFilter === "archived" ? "active" : ""}`}
                    onClick={() => handleStatusFilterChange("archived")}
                  >
                    Archived ({getStatusCount("archived")})
                  </button>
                </div>
              </div>
            )}
        </div>
        {/* Regular ideas section */}
        <div
          className={`ideas-list ${viewMode === "list" ? "list-view" : "grid-view"}`}
        >
          {filteredIdeas.map((idea) =>
            viewMode === "list" ? (
              <IdeaListItem
                key={`list-${idea.id}`}
                idea={idea}
                onBookmark={handleBookmark}
                isMyIdea={false}
              />
            ) : (
              <IdeaGridItem
                key={`grid-${idea.id}`}
                idea={idea}
                onBookmark={handleBookmark}
              />
            ),
          )}
        </div>
        {activeFilter === "all" && hasMoreIdeas() && (
          <div className="calls-action">
            {loadMore ? (
              <Loader message="Loading" />
            ) : (
              <button
                ref={loadMoreButtonRef}
                className="calls-loadMore"
                onClick={handleLoadMore}
                disabled={loadMore}
              >
                Load more
              </button>
            )}
          </div>
        )}
      </div>

      <FiltersDrawer
        isOpen={isFiltersDrawerOpen}
        onClose={() => setIsFiltersDrawerOpen(false)}
        onApply={handleFiltersApply}
        selectedFilters={selectedFilters}
      />
    </div>
  );
};
